import React, { useMemo, useState } from 'react';
import { 
  Trash2, 
  Star,
  AlertCircle,
  Package,
  User,
  DollarSign,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { Button, SearchSelect, StatsGrid, PillToggle } from '../../../components/shared';
import DataTable from '../../../components/shared/DataTable';
import ServiceDetailsModal from '../../../components/vendor/ServiceDetailsModal';
import { useToastContext } from "../../../contexts/ToastContext";
import { 
  useAdminVendorServices, 
  useAdminVendorServiceStatistics,
  useToggleVendorServiceStatus,
  useToggleVendorServiceFeatured,
  useDeleteVendorService
} from '../../../services/adminVendorServiceService';
import { useAdminVendors } from '../../../hooks/useAdminVendors';

const AdminVendorServices = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [featuredFilter, setFeaturedFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const { success, error: showError } = useToastContext();

  // Query parameters - no pagination since DataTable handles it
  const queryParams = {
    search: searchTerm || undefined,
    is_active: statusFilter === 'all' ? undefined : statusFilter === 'active',
    is_featured: featuredFilter === 'all' ? undefined : featuredFilter === 'featured',
    service_category_id: categoryFilter === 'all' ? undefined : categoryFilter,
    vendor_id: vendorFilter === 'all' ? undefined : vendorFilter,
    sort_by: 'created_at',
    sort_order: 'desc'
  };

  const { data: vendorsResponse } = useAdminVendors({ per_page: 500, sort_by: 'created_at', sort_order: 'desc' });

  const vendorOptions = useMemo(() => {
    const vendors = vendorsResponse?.data?.data || vendorsResponse?.data || [];
    if (!Array.isArray(vendors)) return [{ value: 'all', label: 'All Vendors' }];

    const options = vendors
      .filter((v) => v && (v.vendor_profile || v.vendorProfile))
      .map((vendor) => {
        const vp = vendor.vendor_profile || vendor.vendorProfile;
        const name = vp?.business_name || vendor.name || 'Vendor';
        const city = vp?.city || '';
        const state = vp?.state || '';
        const loc = [city, state].filter(Boolean).join(', ');
        return {
          value: vp.id,
          label: loc ? `${name} — ${loc}` : name,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    return [{ value: 'all', label: 'All Vendors' }, ...options];
  }, [vendorsResponse]);

  // Fetch data
  const { 
    data: servicesData, 
    isLoading: loading, 
    error: servicesError,
    refetch 
  } = useAdminVendorServices(queryParams);

  const { 
    data: statisticsData, 
    isLoading: statsLoading 
  } = useAdminVendorServiceStatistics();

  // Extract data
  const services = servicesData?.data || [];
  const statistics = statisticsData?.data;

  // Mutations
  const toggleStatusMutation = useToggleVendorServiceStatus({
    onSuccess: () => {
      success('Service status updated successfully');
    },
    onError: (error) => {
      showError(error.message || 'Failed to update service status');
    }
  });

  const toggleFeaturedMutation = useToggleVendorServiceFeatured({
    onSuccess: () => {
      success('Service featured status updated successfully');
    },
    onError: (error) => {
      showError(error.message || 'Failed to update featured status');
    }
  });

  const deleteServiceMutation = useDeleteVendorService({
    onSuccess: () => {
      success('Service deleted successfully');
    },
    onError: (error) => {
      showError(error.message || 'Failed to delete service');
    }
  });

  const isStatusToggling = (serviceId) =>
    toggleStatusMutation.isPending && toggleStatusMutation.variables === serviceId;

  const isFeaturedToggling = (serviceId) =>
    toggleFeaturedMutation.isPending && toggleFeaturedMutation.variables === serviceId;

  const handleToggleStatus = (serviceId) => {
    toggleStatusMutation.mutate(serviceId);
  };

  const handleToggleFeatured = (serviceId) => {
    toggleFeaturedMutation.mutate(serviceId);
  };

  const handleDeleteService = (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service? This action cannot be undone.')) {
      deleteServiceMutation.mutate(serviceId);
    }
  };

  const handleRowClick = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  // Define columns for DataTable (reduced for better UX)
  const columns = [
    {
      key: 'service_name',
      label: 'Service',
      sortable: true,
      searchable: true,
      render: (service) => (
        <div className="flex items-center cursor-pointer" onClick={() => handleRowClick(service)}>
          <div className="flex-shrink-0 h-10 w-10">
            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center">
              <Package className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors">
              {service.service_name}
              
            </div>
            <div className="text-sm text-gray-500 truncate max-w-xs">
              {service.description}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'vendor',
      label: 'Vendor',
      sortable: false,
      searchable: true,
      render: (service) => (
        <div className="flex items-center cursor-pointer" onClick={() => handleRowClick(service)}>
          <div className="flex-shrink-0 h-8 w-8">
            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
              <User className="h-4 w-4 text-gray-600" />
            </div>
          </div>
          <div className="ml-3">
            <div className="text-sm font-medium text-gray-900">
              {service.vendor_profile?.user?.name || 'Unknown'}
            </div>
            <div className="text-sm text-gray-500">
              {service.service_category?.category_name || 'Unknown'}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'base_price',
      label: 'Price',
      sortable: true,
      render: (service) => (
        <div className="flex items-center cursor-pointer" onClick={() => handleRowClick(service)}>
          <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
          <span className="text-sm font-medium text-gray-900">
            ${parseFloat(service.base_price).toFixed(2)}
          </span>
        </div>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      render: (service) => (
        <PillToggle
          checked={Boolean(service.is_active)}
          onChange={() => handleToggleStatus(service.id)}
          loading={isStatusToggling(service.id)}
          variant="success"
          onLabel="On"
          offLabel="Off"
          ariaLabel={`${service.service_name} active status`}
        />
      ),
    },
    {
      key: 'is_featured',
      label: 'Featured',
      sortable: true,
      render: (service) => (
        <PillToggle
          checked={Boolean(service.is_featured)}
          onChange={() => handleToggleFeatured(service.id)}
          loading={isFeaturedToggling(service.id)}
          variant="amber"
          onLabel="Yes"
          offLabel="No"
          ariaLabel={`${service.service_name} featured status`}
        />
      ),
    },
  ];

  const actions = [
    {
      label: 'Delete',
      icon: Trash2,
      onClick: (service) => handleDeleteService(service.id),
      title: 'Delete Service',
      variant: 'danger',
      size: 'sm',
    },
  ];

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'status':
        setStatusFilter(value);
        break;
      case 'category':
        setCategoryFilter(value);
        break;
      case 'featured':
        setFeaturedFilter(value);
        break;
      case 'vendor':
        setVendorFilter(value);
        break;
      default:
        break;
    }
  };

  // Handle API errors
  if (servicesError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">Error Loading Services</h3>
          <p className="mt-1 text-sm text-gray-500">
            {servicesError.message || 'Failed to load vendor services'}
          </p>
          <div className="mt-6">
            <Button onClick={() => refetch()}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading vendor services...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Vendor Services</h1>
            <p className="mt-2 text-gray-600">Manage all vendor services across the platform</p>
          </div>
        </div>

        {/* Statistics Cards */}
        {statistics && !statsLoading && (
          <StatsGrid
            stats={[
              {
                key: 'total',
                title: 'Total Services',
                value: statistics.total_services,
                icon: Package,
                variant: 'default'
              },
              {
                key: 'active',
                title: 'Active Services',
                value: statistics.active_services,
                icon: CheckCircle,
                variant: 'success'
              },
              {
                key: 'inactive',
                title: 'Inactive Services',
                value: statistics.inactive_services,
                icon: XCircle,
                variant: 'danger'
              },
              {
                key: 'featured',
                title: 'Featured Services',
                value: statistics.featured_services,
                icon: Star,
                variant: 'warning'
              }
            ]}
            columns={4}
            className="mb-8"
          />
        )}

        {/* Filters */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <SearchSelect
                label="Vendor"
                value={vendorFilter}
                onChange={(value) => handleFilterChange('vendor', value)}
                options={vendorOptions}
                placeholder="All vendors"
                searchable
                size="md"
              />
            </div>

            <div>
              <SearchSelect
                label="Status"
                value={statusFilter}
                onChange={(value) => handleFilterChange('status', value)}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' }
                ]}
                placeholder="Select status"
                searchable={false}
                size="md"
              />
            </div>

            <div>
              <SearchSelect
                label="Featured"
                value={featuredFilter}
                onChange={(value) => handleFilterChange('featured', value)}
                options={[
                  { value: 'all', label: 'All Services' },
                  { value: 'featured', label: 'Featured Only' },
                  { value: 'regular', label: 'Regular Only' }
                ]}
                placeholder="Select featured status"
                searchable={false}
                size="md"
              />
            </div>

            <div>
              <SearchSelect
                label="Category"
                value={categoryFilter}
                onChange={(value) => handleFilterChange('category', value)}
                options={[
                  { value: 'all', label: 'All Categories' }
                  // Add categories dynamically when available
                ]}
                placeholder="Select category"
                searchable={false}
                size="md"
              />
            </div>
          </div>
        </div>

        {/* Services DataTable */}
        <DataTable
          data={services}
          columns={columns}
          actions={actions}
          loading={loading}
          searchable={true}
          filterable={false}
          sortable={true}
          selectable={false}
          pageSize={15}
          showPagination={true}
          searchPlaceholder="Search services..."
          emptyMessage="No vendor services found."
          className="mb-6"
        />

        {/* Service Details Modal */}
        <ServiceDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          service={selectedService}
        />
      </div>
    </div>
  );
};

export default AdminVendorServices;
